import type { ApiResponse, ProblemDetail } from './types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

// ─── Error Class ──────────────────────────────────────────────────────────────

export class ApiError extends Error {
  readonly problem: ProblemDetail;
  readonly status: number;

  constructor(problem: ProblemDetail) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
    this.problem = problem;
    this.status = problem.status;
  }
}

// ─── Core Fetch Wrapper ───────────────────────────────────────────────────────

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = `${BASE_URL}${path}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // send/receive cookies for session management
  });

  if (!response.ok) {
    let problem: ProblemDetail;
    try {
      problem = (await response.json()) as ProblemDetail;
    } catch {
      problem = {
        type: 'about:blank',
        title: response.statusText || 'Request failed',
        status: response.status,
        detail: `HTTP ${response.status}: ${response.statusText}`,
      };
    }
    throw new ApiError(problem);
  }

  // 204 No Content
  if (response.status === 204) {
    return { data: null as unknown as T };
  }

  const body = (await response.json()) as ApiResponse<T>;
  return body;
}

// ─── HTTP Method Helpers ──────────────────────────────────────────────────────

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return request<T>(`${path}${buildQuery(params)}`, { method: 'GET' });
  },

  async getRaw<T>(path: string, params?: Record<string, unknown>): Promise<T> {
    const response = await request<T>(`${path}${buildQuery(params)}`, { method: 'GET' });
    return response.data;
  },

  post<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(path: string, body?: unknown): Promise<ApiResponse<T>> {
    return request<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  delete<T = void>(path: string): Promise<ApiResponse<T>> {
    return request<T>(path, { method: 'DELETE' });
  },
};
